import React, { useState, useCallback, useRef } from 'react'

interface FileWithPreview extends File {
  id: string
  preview: string
}

interface InitialFileWithPreview {
  id: string
  name: string
  size: number
  type: string
  preview: string
}

interface InitialFile {
  id: string
  name: string
  size: number
  type: string
  url: string
}

interface UseFileUploadProps {
  accept?: string
  multiple?: boolean
  initialFiles?: InitialFile[]
}

export function useFileUpload({
  accept = '*/*',
  multiple = false,
  initialFiles = [],
}: UseFileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  // Convert initial files to preview format
  const initialFilesWithPreview: InitialFileWithPreview[] = initialFiles.map(
    file => ({
      ...file,
      preview: file.url,
    })
  )

  const [files, setFiles] = useState<
    (FileWithPreview | InitialFileWithPreview)[]
  >(initialFilesWithPreview)

  const addFiles = useCallback(
    (newFiles: FileList) => {
      Array.from(newFiles).forEach(file => {
        const reader = new FileReader()
        reader.onload = e => {
          if (e.target?.result) {
            const fileWithPreview = file as FileWithPreview
            fileWithPreview.id = Math.random().toString(36).substring(7)
            fileWithPreview.preview = e.target.result as string

            setFiles(prev =>
              multiple ? [...prev, fileWithPreview] : [fileWithPreview]
            )
          }
        }
        reader.readAsDataURL(file)
      })
    },
    [multiple]
  )

  const removeFile = useCallback((fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId))
  }, [])

  const openFileDialog = useCallback(() => {
    inputRef.current?.click()
  }, [])

  const getInputProps = useCallback(
    () => ({
      ref: inputRef,
      type: 'file' as const,
      accept,
      multiple,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
          addFiles(e.target.files)
        }
      },
    }),
    [accept, multiple, addFiles]
  )

  return [
    { files },
    { addFiles, removeFile, openFileDialog, getInputProps },
  ] as const
}
